import { useEffect, useState } from 'react';
import { Switch } from '@accure/ui';
import { ShieldCheck, ShieldOff } from 'lucide-react';

import { useMe } from '@/hooks/use-me';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/siteview/components/ui/card';

import { UpdateMfaStatusEnroll, UpdateMfaStatusUnenroll } from '../mutations';

export function MultiFactorAuthSection() {
  const [mfaToggleText, setMfaToggleText] = useState('MFA Disabled');
  const [isMfaEnabled, setIsMfaEnabled] = useState(false);

  const { isMfaActive } = useMe();
  const updateMfaStatusEnrollMutation = UpdateMfaStatusEnroll.useMutation();
  const updateMfaStatusUnenrollMutation = UpdateMfaStatusUnenroll.useMutation();

  // Update local state when query data changes
  useEffect(() => {
    if (isMfaActive) {
      setIsMfaEnabled(isMfaActive);
      setMfaToggleText(isMfaActive ? 'MFA Enabled' : 'MFA Disabled');
    }
  }, [isMfaActive]);

  // Show saving state when mutation is loading
  const displayText = updateMfaStatusEnrollMutation.isLoading
    ? 'Saving...'
    : mfaToggleText;

  const handleMfaToggleClick = async (newMfaState: boolean) => {
    try {
      if (newMfaState) {
        const result = await updateMfaStatusEnrollMutation.mutateAsync();
        window.open(result.enrollmentTicketUrl, '_blank');
      } else {
        await updateMfaStatusUnenrollMutation.mutateAsync();
      }
      setIsMfaEnabled(newMfaState);
      setMfaToggleText(newMfaState ? 'MFA Enabled' : 'MFA Disabled');
    } catch (error) {
      console.error('Failed to update MFA status:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Multi-Factor Authentication (MFA)</CardTitle>
        <CardDescription>
          Add an extra layer of security to your account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              {isMfaEnabled ? (
                <ShieldCheck className="size-4 text-primary-400" />
              ) : (
                <ShieldOff className="size-4 text-gray-400" />
              )}
              <span className="font-medium">{displayText}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Enable MFA to secure your account with an additional verification
              step
            </p>
          </div>
          <Switch
            value={isMfaEnabled}
            onChange={handleMfaToggleClick}
            disabled={updateMfaStatusEnrollMutation.isLoading}
            data-testid="multi-factor-authentication-toggle"
          />
        </div>
        {isMfaEnabled && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
            <p className="text-sm text-primary-700">
              MFA is active. You'll be prompted for additional verification when
              signing in.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
